// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, { ChangeEvent, FC, forwardRef, ReactNode, Ref } from 'react';

import { BaseProps } from '../Base';
import { CheckboxProps } from '../Checkbox';
import { InputProps } from '../Input';
import Label from '../Label';
import { RadioProps } from '../Radio';
import { RadioGroupProps } from '../RadioGroup';
import { SelectProps } from '../Select';
import { TextareaProps } from '../Textarea';
import { StyledFormField, StyledRequiredLabel } from './Styled';
import useUniqueId from '../../../hooks/useUniqueId';
import { RefCallBack } from 'react-hook-form';

export type FieldType =
  | FC<InputProps>
  | FC<SelectProps>
  | FC<TextareaProps>
  | FC<CheckboxProps>
  | FC<RadioGroupProps>
  | FC<RadioProps>;

export interface FieldProps {
  /** The callback fired when the state is changed. */
  onChange(event: ChangeEvent): void;
  /** The label of the field. */
  label: string;
  /** The sub label of the field. */
  subLabel?: ReactNode | ReactNode[];
  /** The type of the field. */
  field: FieldType;
  /** The informational text in the field. */
  infoText?: string;
  /** Whether or not the error text is shown. */
  error?: boolean;
  /** The error text in the field. */
  errorText?: string;
  /** Additional props for field component. */
  fieldProps?: any;
  /** The value of the field. */
  value: string;
  /** Whether or not the field is checked. */
  checked?: boolean;
  /** Options for some fields, e.g. radio group or select. */
  options?: string[] | object[];
  /** React-hook-formのRef */
  rhfRef?: RefCallBack;
  /** Requiredの表示をするかどうかのフラグ */
  showRequiredLabel?: boolean;
  /** FormFieldのSuffixラベル */
  suffixLabel?: string;
  /** ラベルの右側の要素 */
  labelRightContent?: ReactNode;
}

export interface LayoutProps {
  /** Specify the layout of the field, it defaults to `stack`. */
  layout?: 'stack' | 'horizontal' | 'input-only';
  error?: boolean;
  /** 大きいサイズの表示フラグ。LP・ウォークスル画面用 */
  displayLarge?: boolean;
}

export interface FormFieldProps extends FieldProps, LayoutProps, BaseProps {}
export interface FieldWrapperProps extends BaseProps, LayoutProps {}

export const FormField = forwardRef(
  (props: FormFieldProps, ref: Ref<HTMLElement>) => {
    const {
      field: Field,
      label,
      subLabel,
      layout = 'stack',
      errorText,
      fieldProps,
      infoText,
      error,
      onChange,
      value,
      checked,
      options,
      className,
      rhfRef,
      showRequiredLabel,
      suffixLabel,
      labelRightContent,
      ...rest
    } = props;

    const displayName = Field.displayName?.toLowerCase() || '';
    const labelId = useUniqueId();
    const descriptionId = useUniqueId();
    const helpText = (error && errorText) || infoText;

    const renderLabel = () => {
      if (layout === 'input-only' && displayName !== 'checkbox') {
        return null;
      }

      if (displayName !== 'radiogroup') {
        return (
          <div className="ch-label-wrapper">
            {showRequiredLabel && (
              <StyledRequiredLabel>必須</StyledRequiredLabel>
            )}
            <Label
              htmlFor={labelId}
              className={`ch-${displayName}-label`}
              suppressHydrationWarning
            >
              {label}
            </Label>
            {labelRightContent && (
              <div className="ch-label-right-content">{labelRightContent}</div>
            )}
          </div>
        );
      }
      return false;
    };

    return (
      <StyledFormField
        layout={layout}
        error={error}
        className={`ch-form-field-${displayName} ${className || ''}`}
        data-testid="form-field"
        {...rest}
      >
        {renderLabel()}
        {subLabel && <div className="ch-sub-label">{subLabel}</div>}
        {displayName === 'radiogroup' ? (
          <fieldset
            aria-describedby={helpText && descriptionId}
            aria-invalid={error}
          >
            {label && <legend>{label}</legend>}
            <Field
              options={options}
              ref={ref}
              rhfRef={rhfRef}
              id={labelId}
              onChange={onChange}
              value={value}
              {...fieldProps}
              suppressHydrationWarning
            />
          </fieldset>
        ) : (
          <div className="ch-form-field-wrapper">
            <Field
              options={options}
              aria-label={(layout === 'input-only' && label) || null}
              aria-describedby={helpText && descriptionId}
              aria-invalid={error}
              ref={ref}
              rhfRef={rhfRef}
              id={labelId}
              onChange={onChange}
              value={value}
              checked={checked}
              {...fieldProps}
              suppressHydrationWarning
            />
            {suffixLabel && (
              <span className="ch-form-field-suffix">{suffixLabel}</span>
            )}
          </div>
        )}
        {helpText && (
          <span className="ch-help-text" id={descriptionId}>
            {helpText}
          </span>
        )}
      </StyledFormField>
    );
  }
);
FormField.displayName = 'FormField';

export default FormField;
